import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  postIncludedFeatureJob,
  getIncludedFeatureJob,
  getActiveIncludedFeatureJob,
} from "../../client/apiClient";

const ACTIVE_STATUSES = ["Pending", "Running"];

export function useIncludedFeatureJob(userId, featureId) {
  //
  // 1. Find an active job when the page loads
  //
  const activeJobQuery = useQuery({
    queryKey: ["includedFeatureActiveJob", featureId],
    enabled: !!featureId,

    queryFn: async () => {
      const response = await getActiveIncludedFeatureJob(featureId);

      if (response.status === 204) {
        return null;
      }

      if (!response.ok) {
        throw new Error("Unable to load active job.");
      }

      return response.json();
    },
  });

  //
  // 2. Start a new import
  //
  const mutation = useMutation({
    mutationFn: () => postIncludedFeatureJob(userId, featureId),
  });

  //
  // 3. Determine which job to monitor
  //
  const jobId = mutation.data?.jobId ?? activeJobQuery.data?.jobId ?? null;

  //
  // 4. Poll the current job
  //
  const jobQuery = useQuery({
    queryKey: ["includedFeatureJob", featureId, jobId],

    enabled: !!featureId && !!jobId,

    queryFn: async () => {
      const response = await getIncludedFeatureJob(featureId, jobId);

      if (!response.ok) {
        throw new Error("Unable to load job.");
      }

      return response.json();
    },

    refetchInterval: (query) => {
      const status = query.state.data?.status;

      return ACTIVE_STATUSES.includes(status) ? 5000 : false;
    },
  });

  //
  // 5. Derived values
  //
  const job = jobQuery.data;

  const isRunning = ACTIVE_STATUSES.includes(job?.status);

  const progress = useMemo(() => {
    if (!job?.total) {
      return 0;
    }

    return (job.processed / job.total) * 100;
  }, [job]);

  //
  // 6. Public API
  //
  return {
    job,
    jobId,

    progress,
    isRunning,

    startImport: () => mutation.mutate(),

    isStarting: mutation.isPending,

    isLoading: activeJobQuery.isLoading || jobQuery.isLoading,

    error: mutation.error || activeJobQuery.error || jobQuery.error,
  };
}
